import { makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LayoutContext } from "../../context/LayoutContext";
import PageEnum from "../../enum/PageEnum";
import { FlightCardTypes } from "../../enum/flights/FlightEnum";
import { GlobalFilterOptionsEnum } from "../../enum/global-filter/GlobalFilterEnum";
import { useApiCall } from "../../hooks/useApiCall";
import { IKpiSettingsResponse } from "../../models/app-settings/AppSettingsModels";
import { useAppDispatch, useAppSelector } from "../../store/StoreHooks";
import { getAirportCode } from "../../store/airport/AirportSelector";
import { screenNameActions } from "../../store/screen/ScreenNameSlice";
import { AIRPORT_CODE_PARAMETER } from "../../utils/common/Constants";
import { FLIGHT_KPI_URLS_TEMPLATE } from "../../utils/flights/FlightConstants";
import {
  getKPIFilterTime,
  getSelectedFlightCardType,
  getUpdatedKpiDataUrls,
} from "../../utils/flights/FlightHelperFunctions";
import MappedInAirport from "../osm-maps/MappedInAirport";
import FlightDemandCapacity from "./flight-demand-capacity/FlightDemandCapacity";
import Flight360Details from "./flight-details/Flight360Details";
import FlightKPIs from "./flight-kpi/FlightKPIs";
import FlightSummaryCard from "./summary-card/FlightSummaryCard";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const kpiTimeURL = process.env.REACT_APP_API_GET_KPI_TIME;

const Flight360: React.FC = () => {
  const classes = useStyles();

  const airport = useAppSelector((state) => getAirportCode(state));
  const previousAirport = useRef(airport);

  const [isScheduleTimeSummaryCard, setScheduleTimeSummaryCard] =
    useState<boolean>(false);
  const [isScheduleTimeChecked, setIsScheduleTimeChecked] =
    useState<boolean>(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const kpiType = queryParams.get("kpiType");
  const [selectedSection, setSelectedSection] = useState<FlightCardTypes>(
    getSelectedFlightCardType(kpiType)
  );

  const {
    isMappedInView,
    globalFilterDate,
    timezone,
    currentTime,
    selectedFilter,
  } = useContext(LayoutContext);

  const kpiTimeConfigUrl = `${baseURL}${kpiTimeURL}`.replace(
    AIRPORT_CODE_PARAMETER,
    airport
  );
  const { data: kpiTimeData, isLoading } = useApiCall<IKpiSettingsResponse>({
    url: kpiTimeConfigUrl,
    dependencies: [airport],
  });

  const kpiConfigData = getUpdatedKpiDataUrls(
    airport,
    timezone,
    currentTime,
    globalFilterDate,
    selectedFilter as GlobalFilterOptionsEnum,
    kpiTimeData?.kpiSettings || [],
    FLIGHT_KPI_URLS_TEMPLATE
  );

  const tableDateFilter = getKPIFilterTime(
    selectedSection,
    kpiConfigData,
    globalFilterDate
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(screenNameActions.setScreenName(PageEnum.BreadcrumbEnum.Flights));
  }, [dispatch]);

  useEffect(() => {
    if (previousAirport.current !== airport) {
      setSelectedSection(FlightCardTypes.SUMMARY);
      previousAirport.current = airport;
    }
  }, [airport]);

  const handleSectionChange = (newSection: FlightCardTypes) => {
    setSelectedSection(newSection);
    setIsScheduleTimeChecked(false);
  };

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedSection === FlightCardTypes.SUMMARY) {
        setScheduleTimeSummaryCard(event.target.checked);
      }
      setIsScheduleTimeChecked(event.target.checked);
    },
    [selectedSection]
  );

  return (
    <>
      {isMappedInView && airport === "XYZ" ? (
        <MappedInAirport />
      ) : (
        <div
          data-testid="flight360"
          className={mergeClasses(classes.flight360Container, "box-shadow-1")}
        >
          <section className="d-flex">
            <FlightSummaryCard
              airport={airport}
              selectedSection={selectedSection}
              onSectionChange={handleSectionChange}
              scheduleTime={isScheduleTimeSummaryCard}
            />
            {isLoading && (
              <Spinner
                labelPosition="below"
                label="Loading..."
                style={{ margin: "auto" }}
              />
            )}
            {!isLoading && (
              <FlightKPIs
                selectedSection={selectedSection}
                airport={airport}
                kpiConfigData={kpiConfigData}
                onSectionChange={handleSectionChange}
              />
            )}
          </section>
          <Flight360Details
            viewType={selectedSection}
            airport={airport}
            tableDateFilter={tableDateFilter}
            isScheduleTimeChecked={isScheduleTimeChecked}
            handleCheckboxChange={handleCheckboxChange}
          />
          {
            /* Temporary code to show Demand & Capacity card for LT timezone */
            timezone === "LT" && <FlightDemandCapacity />
          }
        </div>
      )}
    </>
  );
};

export default Flight360;

const useStyles = makeStyles({
  flight360Container: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "10px",
  },
});
